import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Flex, FormControl, FormLabel, HStack, Input, Select, InputGroup, InputRightElement, Switch, VStack, useToast, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { createUsersApi } from '../../../../utils/api-interface'
import { UserDetailsResponse, Address, UserDocumentsInfoDocumentTypeEnum, UserUpdateRequest, DashboardProfile } from '../../../../services/network'
import { PaymentInfo } from '../../../../components/PaymentInfo/PaymentInfo'
import GooglePlacesAutocomplete, { geocodeByAddress } from 'react-google-places-autocomplete'
import { SingleDatepicker } from '../../../../single'
import { log } from '../../../../utils/logger'
import { Option } from 'react-google-places-autocomplete/build/types'
import dayjs from 'dayjs'
import { getErrorMessage } from '../../../../utils/utils'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/store'

export interface IUserVerificationProps {
  onUserUpdate: () => void
  isUserDeleted: boolean
  user: UserDetailsResponse | undefined
}

function UserVerification({ onUserUpdate, isUserDeleted, user }: IUserVerificationProps): JSX.Element {
  const { t } = useTranslation()
  const { id } = useParams()
  const toast = useToast()

  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true)
  const [userUpdateRequest, setUserUpdateRequest] = useState<UserUpdateRequest>()

  const [loadingUpdate, isLoadingUpdate] = useState<boolean>(false)

  const defaultTraderName = useMemo(() => {
    const firstName = user?.firstName ?? ''
    const lastName = user?.lastName ?? ''
    return `${firstName} ${lastName}`.trim()
  }, [user?.firstName, user?.lastName])

  const dashboardUser: DashboardProfile | null = useSelector((state: RootState) => state.dashboardUser.dashboardUser)
  const canUpdate = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title === process.env.REACT_APP_ROLE_ADMINISTRATOR || dashboardUser.role.title === process.env.REACT_APP_ROLE_BACKOFFICE
  }, [dashboardUser])

  const controlEnabled = useMemo(() => {
    if (loadingUpdate) {
      return false
    }
    return true
  }, [loadingUpdate])

  const handleDocumentFiscalCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          documents: {
            ...userUpdateRequest?.documents,
            fiscalCode: e.target.value
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleLegalNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          invoicing: {
            ...userUpdateRequest?.invoicing,
            legalName: e.target.value ?? ''
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleInvoiceDestinationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          invoicing: {
            ...userUpdateRequest?.invoicing,
            invoiceDestination: e.target.value ?? ''
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleVatNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          documents: {
            ...userUpdateRequest?.documents,
            vatNumber: e.target.value
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleInvoicingFiscalCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          invoicing: {
            ...userUpdateRequest?.invoicing,
            fiscalCode: e.target.value
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleInvoicingVatNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          invoicing: {
            ...userUpdateRequest?.invoicing,
            vatNumber: e.target.value
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleTraderDefaultEnabledChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        const checked = e.target.checked
        const currentTrader = userUpdateRequest?.trader
        const traderAddressSource = currentTrader?.address ?? user?.trader?.address ?? userUpdateRequest?.address ?? user?.address
        const prefilledTraderAddress: Address | undefined = traderAddressSource
          ? {
              city: traderAddressSource.city ?? '',
              country: traderAddressSource.country ?? '',
              street: traderAddressSource.street ?? '',
              postalCode: traderAddressSource.postalCode ?? '',
              state: traderAddressSource.state ?? ''
            }
          : undefined

        setUserUpdateRequest({
          ...userUpdateRequest,
          trader: {
            ...currentTrader,
            defaultEnabled: checked,
            displayName: checked ? (currentTrader?.displayName ?? user?.trader?.displayName ?? defaultTraderName) : currentTrader?.displayName,
            email: checked ? (currentTrader?.email ?? user?.trader?.email ?? user?.email) : currentTrader?.email,
            phoneNumber: checked ? (currentTrader?.phoneNumber ?? user?.trader?.phoneNumber ?? user?.phoneNumber) : currentTrader?.phoneNumber,
            fiscalCode: checked ? (currentTrader?.fiscalCode ?? user?.trader?.fiscalCode ?? user?.documents?.fiscalCode) : currentTrader?.fiscalCode,
            vatNumber: checked ? (currentTrader?.vatNumber ?? user?.trader?.vatNumber ?? user?.documents?.vatNumber) : currentTrader?.vatNumber,
            address: checked ? prefilledTraderAddress : currentTrader?.address
          }
        })
      }
    },
    [canUpdate, defaultTraderName, user, userUpdateRequest]
  )

  const updateTraderField = useCallback(
    (field: 'displayName' | 'email' | 'phoneNumber' | 'fiscalCode' | 'vatNumber', value: string): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          trader: {
            ...userUpdateRequest?.trader,
            [field]: value
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const updateTraderAddressField = useCallback(
    (field: keyof Address, value: string): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          trader: {
            ...userUpdateRequest?.trader,
            address: {
              city: userUpdateRequest?.trader?.address?.city ?? '',
              country: userUpdateRequest?.trader?.address?.country ?? '',
              street: userUpdateRequest?.trader?.address?.street ?? '',
              postalCode: userUpdateRequest?.trader?.address?.postalCode ?? '',
              state: userUpdateRequest?.trader?.address?.state ?? '',
              [field]: value
            }
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleTraderDisplayNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderField('displayName', e.target.value ?? ''),
    [updateTraderField]
  )

  const handleTraderEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderField('email', e.target.value ?? ''),
    [updateTraderField]
  )

  const handleTraderPhoneNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderField('phoneNumber', e.target.value ?? ''),
    [updateTraderField]
  )

  const handleTraderFiscalCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderField('fiscalCode', e.target.value ?? ''),
    [updateTraderField]
  )

  const handleTraderVatNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderField('vatNumber', e.target.value ?? ''),
    [updateTraderField]
  )

  const handleTraderStreetChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderAddressField('street', e.target.value ?? ''),
    [updateTraderAddressField]
  )

  const handleTraderPostalCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderAddressField('postalCode', e.target.value ?? ''),
    [updateTraderAddressField]
  )

  const handleTraderCityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderAddressField('city', e.target.value ?? ''),
    [updateTraderAddressField]
  )

  const handleTraderStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderAddressField('state', e.target.value ?? ''),
    [updateTraderAddressField]
  )

  const handleTraderCountryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => updateTraderAddressField('country', e.target.value ?? ''),
    [updateTraderAddressField]
  )

  const handleDocumentNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          documents: {
            ...userUpdateRequest?.documents,
            documentNumber: e.target.value
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleDocumentTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          documents: {
            ...userUpdateRequest?.documents,
            documentType: e.target.value as UserDocumentsInfoDocumentTypeEnum
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const setDocumentExpirationDate = useCallback(
    (date: Date) => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          documents: {
            ...userUpdateRequest?.documents,
            documentExpirationDate: dayjs(date).toISOString()
          }
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const updateProfile = useCallback(async (): Promise<void> => {
    if (!id || !userUpdateRequest || !controlEnabled || !canUpdate) {
      return
    }
    isLoadingUpdate(true)
    try {
      const usersApi = createUsersApi()
      log.debug(userUpdateRequest)
      const response = await usersApi.updateUser(id, userUpdateRequest)
      log.debug(response)
      onUserUpdate()
      toast({ title: t('update_user_success'), status: 'success', duration: 4000, isClosable: true })
    } catch (error: any) {
      toast({ title: t('error_update_user'), description: getErrorMessage(error), status: 'error', duration: 4000, isClosable: true })
    }
    isLoadingUpdate(false)
  }, [canUpdate, controlEnabled, id, onUserUpdate, t, toast, userUpdateRequest])

  const handleUpdate = useCallback((): void => {
    updateProfile()
  }, [updateProfile])

  const [option, setOption] = useState<Option>()
  const [textInput, setTextInput] = useState<string>('')
  const [textInputInvoicing, setTextInputInvoicing] = useState<string>('')
  const [optionInvoicing, setOptionInvoicing] = useState<Option>()

  const handleInputChange = useCallback(
    (input: string, prev: any) => {
      if (canUpdate) {
        if (prev.action !== 'input-change') {
          if (textInput === '') {
            setTextInput(prev.prevInputValue)
          } else {
            setTextInput(textInput)
          }
          return
        }
        setTextInput(input)
      }
    },
    [canUpdate, textInput]
  )

  const handleInputInvoicingChange = useCallback(
    (input: string, prev: any) => {
      if (canUpdate) {
        if (prev.action !== 'input-change') {
          if (textInputInvoicing === '') {
            setTextInputInvoicing(prev.prevInputValue)
          } else {
            setTextInputInvoicing(textInputInvoicing)
          }
          return
        }
        setTextInputInvoicing(input)
      }
    },
    [canUpdate, textInputInvoicing]
  )

  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false)
  const handleInputChange2 = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setMenuIsOpen(true)
    setTextInput(e.currentTarget.value)
  }, [])

  const [menuIsOpenInvoicing, setMenuIsOpenInvoicing] = useState<boolean>(false)
  const handleInputChange3 = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setMenuIsOpenInvoicing(true)
    setTextInputInvoicing(e.currentTarget.value)
  }, [])

  useEffect(() => {
    log.debug(`selected option: ${JSON.stringify(option)}`)
    if (option) {
      setTextInput(option?.label)
    }
    if (optionInvoicing) {
      setTextInputInvoicing(optionInvoicing?.label)
    }
  }, [option, optionInvoicing])

  const validAddress = (address: Address): boolean => {
    return Object.entries(address)
      .filter(([key]) => key !== 'state')
      .every(([, value]) => value !== '')
  }

  const missingComponentAddress = useCallback(
    (address: Address): string[] => {
      return Object.entries(address)
        .filter(([key, value]) => key !== 'state' && value === '')
        .map(([key]) => t(key))
    },
    [t]
  )

  const handleSelectAddress = useCallback(
    async (value: any): Promise<void> => {
      if (canUpdate) {
        const results = await geocodeByAddress(value.label)
        const addressComponents = results[0].address_components

        const newAddress: Address = {
          city: '',
          country: '',
          street: '',
          postalCode: '',
          state: ''
        }

        for (const component of addressComponents) {
          const types = component.types
          if (types.includes('locality') || types.includes('administrative_area_level_3')) {
            if (newAddress.city === '') {
              newAddress.city = component.long_name
            }
          }
          if (types.includes('country')) {
            newAddress.country = component.short_name
          }
          if (types.includes('route')) {
            newAddress.street = value.label
          }
          if (types.includes('postal_code')) {
            newAddress.postalCode = component.long_name
          }
          if (types.includes('administrative_area_level_2')) {
            newAddress.state = component.short_name
          }
        }

        if (!validAddress(newAddress)) {
          toast({
            title: t('invalid_address', { missing: missingComponentAddress(newAddress).join(',') }),
            status: 'error',
            duration: 2000,
            isClosable: true
          })
          return
        }

        if (user) {
          if (user.documents?.nationality) {
            if (user.documents.nationality !== newAddress.country) {
              toast({
                title: t("Can't change nationality of user"),
                status: 'error',
                duration: 2000,
                isClosable: true
              })
            }
          }
        }

        setUserUpdateRequest({
          ...userUpdateRequest,
          documents: {
            ...userUpdateRequest?.documents,
            nationality: newAddress.country
          },
          address: newAddress
        })
        setOption(value as Option)
        setTextInput(value.label)
      }
    },
    [canUpdate, missingComponentAddress, t, toast, user, userUpdateRequest]
  )

  const handleSelectInvoicingAddress = useCallback(
    async (value: any): Promise<void> => {
      if (canUpdate) {
        const results = await geocodeByAddress(value.label)
        const addressComponents = results[0].address_components

        const newAddress: Address = {
          city: '',
          country: '',
          street: '',
          postalCode: '',
          state: ''
        }

        for (const component of addressComponents) {
          const types = component.types
          if (types.includes('locality') || types.includes('administrative_area_level_3')) {
            if (newAddress.city === '') {
              newAddress.city = component.long_name
            }
          }
          if (types.includes('country')) {
            newAddress.country = component.short_name
          }
          if (types.includes('route')) {
            newAddress.street = value.label
          }
          if (types.includes('postal_code')) {
            newAddress.postalCode = component.long_name
          }
          if (types.includes('administrative_area_level_2')) {
            newAddress.state = component.short_name
          }
        }

        if (!validAddress(newAddress)) {
          toast({
            title: t('invalid_address', { missing: missingComponentAddress(newAddress).join(',') }),
            status: 'error',
            duration: 2000,
            isClosable: true
          })
          return
        }

        setUserUpdateRequest({
          ...userUpdateRequest,
          invoicing: {
            ...userUpdateRequest?.invoicing,
            legalAddress: newAddress
          }
        })
        setOptionInvoicing(value as Option)
        setTextInputInvoicing(value.label)
      }
    },
    [canUpdate, missingComponentAddress, t, toast, userUpdateRequest]
  )

  useEffect(() => {
    if (user?.address) {
      if (user?.address.street && user?.address.street !== '') {
        setTextInput(user?.address.street)
      }
    }
  }, [user?.address])

  useEffect(() => {
    if (user?.invoicing?.legalAddress) {
      if (user?.invoicing?.legalAddress.street && user?.invoicing?.legalAddress.street !== '') {
        setTextInputInvoicing(user?.invoicing?.legalAddress.street)
      }
    }
  }, [user?.invoicing?.legalAddress])

  useEffect(() => {
    if (user) {
      setUserUpdateRequest({
        documents: user.documents,
        address: user.address,
        invoicing: user.invoicing,
        trader: user.trader
      })
      setSaveButtonDisabled(false)
    }
  }, [user])

  return (
    <Flex direction={'column'} rowGap={'24px'}>
      <Text fontSize='18px' fontWeight='700' lineHeight='24px'>
        {t('profile_documents_group')}
      </Text>

      {/* Residence Address */}
      <HStack>
        <FormControl id='address' width={'50%'}>
          <FormLabel variant={'primary'}>{t('address')}</FormLabel>
          {!menuIsOpen ? (
            <Input bg='white' value={textInput} color={'black'} onChange={handleInputChange2} disabled={!canUpdate || isUserDeleted} />
          ) : (
            <GooglePlacesAutocomplete
              apiKey={process.env.REACT_APP_GOOGLE_PLACES_WEB_API_KEY}
              autocompletionRequest={{
                componentRestrictions: user?.address?.country ? { country: [user?.address?.country] } : undefined
              }}
              debounce={400}
              minLengthAutocomplete={4}
              selectProps={{
                autoFocus: true,
                defaultInputValue: user?.address?.street,
                value: option,
                options: option ? [option] : undefined,
                inputValue: textInput,
                onMenuClose: (): void => {
                  setMenuIsOpen(false)
                },
                onChange: handleSelectAddress,
                onInputChange: handleInputChange,
                styles: {
                  input: (provided) => ({
                    ...provided,
                    backgroundColor: 'white',
                    color: 'black'
                  })
                }
              }}
            />
          )}
        </FormControl>
        <FormControl id='nationality' width={'12%'}>
          <FormLabel variant={'primary'}>{t('nationality')}</FormLabel>
          <Input bg='white' value={userUpdateRequest?.address?.country ?? ''} color={'black'} disabled={true} />
        </FormControl>
        <FormControl id='state' width={'12%'}>
          <FormLabel variant={'primary'}>{t('state')}</FormLabel>
          <Input bg='white' value={userUpdateRequest?.address?.state ?? ''} color={'black'} disabled={true} />
        </FormControl>
        <FormControl id='state' width={'25%'}>
          <FormLabel variant={'primary'}>{t('residence_city')}</FormLabel>
          <Input bg='white' value={userUpdateRequest?.address?.city ?? ''} color={'black'} disabled={true} />
        </FormControl>
      </HStack>

      {/* Documents */}
      <HStack>
        <FormControl id='documentType' width={'50%'}>
          <FormLabel variant={'primary'}>{t('document_type')}</FormLabel>
          <Select
            variant={'primary'}
            placeholder='Select Document Type'
            onChange={handleDocumentTypeChange}
            value={userUpdateRequest?.documents?.documentType}
            disabled={!canUpdate || isUserDeleted}>
            {Object.entries(UserDocumentsInfoDocumentTypeEnum).map(([key, value]) => (
              <option key={key} value={value}>
                {t(value)}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl id='documentNumber' width={'25%'}>
          <FormLabel variant={'primary'}>{t('document_number')}</FormLabel>
          <Input
            bg='white'
            value={userUpdateRequest?.documents?.documentNumber}
            color={'black'}
            onChange={handleDocumentNumberChange}
            disabled={!canUpdate || isUserDeleted}
          />
        </FormControl>
        <FormControl id='documentExpirationDate' width={'24%'}>
          <FormLabel variant={'primary'}>{t('document_expiration_date')}</FormLabel>
          <InputGroup isolation={'auto'}>
            <Input
              bg={'white'}
              variant={!canUpdate || isUserDeleted ? undefined : 'date'}
              value={dayjs(userUpdateRequest?.documents?.documentExpirationDate).format(`DD/MM/YYYY`)}
              color={'black'}
              disabled={!canUpdate || isUserDeleted}
            />
            {canUpdate && !isUserDeleted && (
              <InputRightElement width={'50px'}>
                <SingleDatepicker
                  name='date-input'
                  date={dayjs(userUpdateRequest?.documents?.documentExpirationDate).toDate()}
                  onDateChange={setDocumentExpirationDate}></SingleDatepicker>
              </InputRightElement>
            )}
          </InputGroup>
        </FormControl>
      </HStack>

      <HStack>
        <FormControl id='documentFiscalCode'>
          <FormLabel variant={'primary'}>{t('fiscal_code')}</FormLabel>
          <Input
            bg='white'
            value={userUpdateRequest?.documents?.fiscalCode !== 'PLACEHOLDER' ? userUpdateRequest?.documents?.fiscalCode?.toUpperCase() : ''}
            color={'black'}
            onChange={handleDocumentFiscalCodeChange}
            disabled={!canUpdate || isUserDeleted}
          />
        </FormControl>
        <FormControl id='documentVatNumber'>
          <FormLabel variant={'primary'}>{t('vatNumber')}</FormLabel>
          <Input
            bg='white'
            value={userUpdateRequest?.documents?.vatNumber?.toUpperCase() ?? ''}
            color={'black'}
            onChange={handleVatNumberChange}
            disabled={!canUpdate || isUserDeleted}
          />
        </FormControl>
      </HStack>

      <Text fontSize='18px' fontWeight='700' lineHeight='24px'>
        {t('invoicing_data_group')}
      </Text>

      {/* Invoicing */}
      {/* Legal Name and SDI/PEC */}
      <HStack>
        <FormControl id='legalName'>
          <FormLabel variant={'primary'}>{t('invoicing.legalName')}</FormLabel>
          <Input
            bg='white'
            value={userUpdateRequest?.invoicing?.legalName}
            color={'black'}
            onChange={handleLegalNameChange}
            disabled={!canUpdate || isUserDeleted}
          />
        </FormControl>
        <FormControl id='sdiOrPec'>
          <FormLabel variant={'primary'}>{t('invoicing.invoiceDestination')}</FormLabel>
          <Input
            bg='white'
            value={userUpdateRequest?.invoicing?.invoiceDestination}
            color={'black'}
            onChange={handleInvoiceDestinationChange}
            disabled={!canUpdate || isUserDeleted}
          />
        </FormControl>
      </HStack>

      {/* Invoicing Address */}
      <HStack>
        <FormControl id='invoicingAddress' width={'50%'}>
          <FormLabel variant={'primary'}>{t('invoicing.legalAddress')}</FormLabel>
          {!menuIsOpenInvoicing ? (
            <Input bg='white' value={textInputInvoicing} color={'black'} onChange={handleInputChange3} disabled={!canUpdate || isUserDeleted} />
          ) : (
            <GooglePlacesAutocomplete
              apiKey={process.env.REACT_APP_GOOGLE_PLACES_WEB_API_KEY}
              autocompletionRequest={{
                componentRestrictions: user?.invoicing?.legalAddress?.country ? { country: [user?.invoicing?.legalAddress?.country] } : undefined
              }}
              debounce={400}
              minLengthAutocomplete={4}
              selectProps={{
                autoFocus: true,
                defaultInputValue: userUpdateRequest?.invoicing?.legalAddress?.street,
                value: optionInvoicing,
                options: optionInvoicing ? [optionInvoicing] : undefined,
                inputValue: textInputInvoicing,
                onMenuClose: (): void => {
                  setMenuIsOpenInvoicing(false)
                },
                onChange: handleSelectInvoicingAddress,
                onInputChange: handleInputInvoicingChange,
                styles: {
                  input: (provided) => ({
                    ...provided,
                    backgroundColor: 'white',
                    color: 'black'
                  })
                }
              }}
            />
          )}
        </FormControl>
        <FormControl id='nationality' width={'12%'}>
          <FormLabel variant={'primary'}>{t('invoicing.nationality')}</FormLabel>
          <Input bg='white' value={userUpdateRequest?.invoicing?.legalAddress?.country ?? ''} color={'black'} disabled={true} />
        </FormControl>
        <FormControl id='state' width={'12%'}>
          <FormLabel variant={'primary'}>{t('invoicing.state')}</FormLabel>
          <Input bg='white' value={userUpdateRequest?.invoicing?.legalAddress?.state ?? ''} color={'black'} disabled={true} />
        </FormControl>
        <FormControl id='state' width={'25%'}>
          <FormLabel variant={'primary'}>{t('invoicing.city')}</FormLabel>
          <Input bg='white' value={userUpdateRequest?.invoicing?.legalAddress?.city ?? ''} color={'black'} disabled={true} />
        </FormControl>
      </HStack>

      {/* VAT or Fiscal Code */}
      <HStack>
        <FormControl id='invoicingFiscalCode'>
          <FormLabel variant={'primary'}>{t('invoicing.fiscalCode')}</FormLabel>
          <Input
            bg='white'
            value={userUpdateRequest?.invoicing?.fiscalCode?.toUpperCase() ?? ''}
            color={'black'}
            onChange={handleInvoicingFiscalCodeChange}
            disabled={!canUpdate || isUserDeleted}
          />
        </FormControl>
        <FormControl id='invoicingVatNumber'>
          <FormLabel variant={'primary'}>{t('invoicing.vatNumber')}</FormLabel>
          <Input
            bg='white'
            value={userUpdateRequest?.invoicing?.vatNumber?.toUpperCase() ?? ''}
            color={'black'}
            onChange={handleInvoicingVatNumberChange}
            disabled={!canUpdate || isUserDeleted}
          />
        </FormControl>
      </HStack>

      <Text fontSize='18px' fontWeight='700' lineHeight='24px'>
        {t('payment_data_group')}
      </Text>

      {/* Payment Methods */}
      <HStack>
        <FormControl id='ibanOwner' width={'50%'}>
          <FormLabel variant={'primary'}>{t('iban_owner')}</FormLabel>
          <Input bg='white' disabled={true} defaultValue={user?.externalAccount?.externalAccountHolderName} color={'black'} />
        </FormControl>
        <FormControl id='iban' width={'24%'}>
          <FormLabel variant={'primary'}>{t('iban')}</FormLabel>
          <Input
            bg='white'
            defaultValue={user?.externalAccount?.externalAccountLast4 ? `************* ${user?.externalAccount?.externalAccountLast4}` : undefined}
            color={'black'}
            disabled={true}
          />
        </FormControl>
        <FormControl id='swiftcode' width={'25%'}>
          <FormLabel variant={'primary'}>{t('swift_code')}</FormLabel>
          {/* this value is not handled */}
          <Input bg='white' defaultValue={undefined} color={'black'} disabled={true} />
        </FormControl>
      </HStack>
      <VStack alignItems={'start'} style={{ height: 172, overflow: 'hidden', width: '100%' }}>
        <FormLabel variant={'primary'}>{t('payment_method')}</FormLabel>
        <Flex direction={'row'} wrap={'wrap'} columnGap={20} overflowY={'scroll'}>
          {user?.paymentInfo?.paymentMethods.map((method, index) => (
            <PaymentInfo key={index} brand={method.brand} last4={method.last4} owner={method.owner} expirationDate={method.expirationDate} />
          ))}
        </Flex>
      </VStack>

      <Text fontSize='18px' fontWeight='700' lineHeight='24px'>
        {t('trader_data_group')}
      </Text>

      <HStack alignItems={'flex-start'}>
        <HStack width={'50%'}>
          <FormControl id='traderDefaultEnabled' width={'auto'} minWidth={'130px'}>
            <FormLabel variant={'primary'}>{t('trader_default_enabled')}</FormLabel>
            <Flex height={'40px'} alignItems={'center'}>
              <Switch
                variant={'primary'}
                isChecked={!!userUpdateRequest?.trader?.defaultEnabled}
                onChange={handleTraderDefaultEnabledChange}
                disabled={!canUpdate || isUserDeleted}></Switch>
            </Flex>
          </FormControl>
          <FormControl id='traderDisplayName' flex={1}>
            <FormLabel variant={'primary'}>{t('trader_display_name')}</FormLabel>
            <Input
              bg='white'
              value={userUpdateRequest?.trader?.displayName ?? ''}
              color={'black'}
              onChange={handleTraderDisplayNameChange}
              disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
            />
          </FormControl>
        </HStack>
        <HStack width={'50%'}>
          <FormControl id='traderEmail' width={'50%'}>
            <FormLabel variant={'primary'}>{t('trader_email')}</FormLabel>
            <Input
              bg='white'
              value={userUpdateRequest?.trader?.email ?? ''}
              color={'black'}
              onChange={handleTraderEmailChange}
              disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
            />
          </FormControl>
          <FormControl id='traderPhoneNumber' width={'50%'}>
            <FormLabel variant={'primary'}>{t('trader_phone_number')}</FormLabel>
            <Input
              bg='white'
              value={userUpdateRequest?.trader?.phoneNumber ?? ''}
              color={'black'}
              onChange={handleTraderPhoneNumberChange}
              disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
            />
          </FormControl>
        </HStack>
      </HStack>
      <HStack>
        <FormControl id='traderFiscalCode' width={'50%'}>
          <FormLabel variant={'primary'}>{t('trader_fiscal_code')}</FormLabel>
          <Input
            bg='white'
            value={userUpdateRequest?.trader?.fiscalCode ?? ''}
            color={'black'}
            onChange={handleTraderFiscalCodeChange}
            disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
          />
        </FormControl>
        <FormControl id='traderVatNumber' width={'50%'}>
          <FormLabel variant={'primary'}>{t('trader_vat_number')}</FormLabel>
          <Input
            bg='white'
            value={userUpdateRequest?.trader?.vatNumber ?? ''}
            color={'black'}
            onChange={handleTraderVatNumberChange}
            disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
          />
        </FormControl>
      </HStack>
      <HStack>
        <HStack width={'50%'}>
          <FormControl id='traderStreet' width={'75%'}>
            <FormLabel variant={'primary'}>{t('trader_street')}</FormLabel>
            <Input
              bg='white'
              value={userUpdateRequest?.trader?.address?.street ?? ''}
              color={'black'}
              onChange={handleTraderStreetChange}
              disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
            />
          </FormControl>
          <FormControl id='traderPostalCode' width={'25%'}>
            <FormLabel variant={'primary'}>{t('trader_postal_code')}</FormLabel>
            <Input
              bg='white'
              value={userUpdateRequest?.trader?.address?.postalCode ?? ''}
              color={'black'}
              onChange={handleTraderPostalCodeChange}
              disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
            />
          </FormControl>
        </HStack>
        <HStack width={'50%'}>
          <FormControl id='traderCountry' width={'25%'}>
            <FormLabel variant={'primary'}>{t('trader_country')}</FormLabel>
            <Input
              bg='white'
              value={userUpdateRequest?.trader?.address?.country ?? ''}
              color={'black'}
              onChange={handleTraderCountryChange}
              disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
            />
          </FormControl>
          <FormControl id='traderState' width={'25%'}>
            <FormLabel variant={'primary'}>{t('trader_state')}</FormLabel>
            <Input
              bg='white'
              value={userUpdateRequest?.trader?.address?.state ?? ''}
              color={'black'}
              onChange={handleTraderStateChange}
              disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
            />
          </FormControl>
          <FormControl id='traderCity' width={'50%'}>
            <FormLabel variant={'primary'}>{t('trader_city')}</FormLabel>
            <Input
              bg='white'
              value={userUpdateRequest?.trader?.address?.city ?? ''}
              color={'black'}
              onChange={handleTraderCityChange}
              disabled={!canUpdate || isUserDeleted || !userUpdateRequest?.trader?.defaultEnabled}
            />
          </FormControl>
        </HStack>
      </HStack>

      {!isUserDeleted && canUpdate && (
        <Flex direction={'row-reverse'}>
          {loadingUpdate ? (
            <>
              <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
                {t('operation_in_progress', { operation: t('update_user_op') })}
              </Text>
            </>
          ) : (
            <>
              <Button variant={'primary'} width={'211px'} onClick={handleUpdate} isDisabled={!controlEnabled && saveButtonDisabled}>
                {t('save')}
              </Button>
            </>
          )}
        </Flex>
      )}
    </Flex>
  )
}

export { UserVerification }
